/**
 * QuickActionsBar Component
 *
 * Quick action buttons row for dashboard views.
 * Provides easy access to common actions: Scan Repo, Upload Docs, View Specs, Run Workflows.
 *
 * Features:
 * - Configurable action cards in a responsive grid
 * - Disabled state with upgrade prompt for tier-gated features
 * - Visual design: Clean cards with icons and descriptions
 * - Supports both legacy 3-action mode and extended mode with custom actions
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React from 'react';
import { Card, Button, Tooltip } from 'flowbite-react';
import {
  HiCode,
  HiDocumentAdd,
  HiDocumentText,
  HiArrowRight,
  HiLockClosed,
  HiSparkles,
  HiSearch,
  HiPlay,
  HiLightningBolt,
  HiShieldCheck,
  HiUserGroup,
  HiCog,
  HiChartBar,
  HiPlus,
} from 'react-icons/hi';
import { FaGithub } from 'react-icons/fa';

// =============================================================================
// TYPES
// =============================================================================

export type QuickActionType =
  | 'scan-repo'
  | 'upload-docs'
  | 'create-spec'
  | 'view-specs'
  | 'run-workflow'
  | 'security-review'
  | 'invite-team'
  | 'view-reports'
  | 'settings'
  | 'connect-github';

export interface QuickAction {
  id: QuickActionType;
  label: string;
  description: string;
  icon: React.ReactNode;
  color: 'blue' | 'green' | 'purple' | 'indigo' | 'gray' | 'amber' | 'red';
  disabled?: boolean;
  disabledMessage?: string;
  badge?: string;
}

// Legacy props interface for backward compatibility
export interface QuickActionsBarLegacyProps {
  /** Callback when Scan Repo action is clicked */
  onScanRepo: () => void;
  /** Callback when Upload Docs action is clicked */
  onUploadDocs: () => void;
  /** Callback when View Specs action is clicked */
  onViewSpecs: () => void;
  /** Whether upload feature is enabled for tier */
  canUpload: boolean;
  /** Whether spec generation is enabled for tier */
  canGenerateSpecs: boolean;
  /** Optional: Whether currently loading */
  isLoading?: boolean;
  /** Optional: Additional CSS classes */
  className?: string;
}

// Extended props interface for new flexible usage
export interface QuickActionsBarExtendedProps {
  /** Unified action click handler */
  onActionClick: (actionId: QuickActionType) => void;
  /** Which actions to show (order matters) */
  visibleActions?: QuickActionType[];
  /** Which actions are disabled */
  disabledActions?: QuickActionType[];
  /** Custom disabled messages per action */
  disabledMessages?: Partial<Record<QuickActionType, string>>;
  /** Badge counts per action */
  badges?: Partial<Record<QuickActionType, string>>;
  /** Compact mode - smaller buttons in a row */
  compact?: boolean;
  /** Loading state */
  isLoading?: boolean;
  /** Additional CSS classes */
  className?: string;
}

// Combined props type - supports both legacy and extended
export type QuickActionsBarProps = QuickActionsBarLegacyProps | QuickActionsBarExtendedProps;

// Type guard to check if props are legacy
function isLegacyProps(props: QuickActionsBarProps): props is QuickActionsBarLegacyProps {
  return 'onScanRepo' in props;
}

// =============================================================================
// DEFAULT ACTIONS CONFIGURATION
// =============================================================================

const defaultActionsConfig: Record<QuickActionType, Omit<QuickAction, 'id' | 'disabled' | 'disabledMessage' | 'badge'>> = {
  'connect-github': {
    label: 'Connect GitHub',
    description: 'Link a GitHub repository to get started',
    icon: <FaGithub className="w-5 h-5" />,
    color: 'gray',
  },
  'scan-repo': {
    label: 'Scan Repository',
    description: 'Connect a GitHub repository for automated code analysis and health scoring.',
    icon: <HiSearch className="w-5 h-5" />,
    color: 'blue',
  },
  'upload-docs': {
    label: 'Upload Documents',
    description: 'Import existing docs, PRDs, meeting notes, and architecture documents.',
    icon: <HiDocumentAdd className="w-5 h-5" />,
    color: 'purple',
  },
  'create-spec': {
    label: 'Create Specification',
    description: 'Start a new specification from template',
    icon: <HiPlus className="w-5 h-5" />,
    color: 'purple',
  },
  'view-specs': {
    label: 'View Specifications',
    description: 'Browse and manage your generated specifications and documentation.',
    icon: <HiDocumentText className="w-5 h-5" />,
    color: 'green',
  },
  'run-workflow': {
    label: 'Run Workflow',
    description: 'Execute an agent workflow',
    icon: <HiLightningBolt className="w-5 h-5" />,
    color: 'amber',
  },
  'security-review': {
    label: 'Security Review',
    description: 'Run a security assessment on your codebase',
    icon: <HiShieldCheck className="w-5 h-5" />,
    color: 'red',
  },
  'invite-team': {
    label: 'Invite Team',
    description: 'Add team members to your organization',
    icon: <HiUserGroup className="w-5 h-5" />,
    color: 'blue',
  },
  'view-reports': {
    label: 'View Reports',
    description: 'Access assessment reports and analytics',
    icon: <HiChartBar className="w-5 h-5" />,
    color: 'indigo',
  },
  'settings': {
    label: 'Settings',
    description: 'Configure your account and preferences',
    icon: <HiCog className="w-5 h-5" />,
    color: 'gray',
  },
};

// Default visible actions for different dashboard states
export const onboardingActions: QuickActionType[] = [
  'scan-repo',
  'upload-docs',
  'view-specs',
];

export const matureClientActions: QuickActionType[] = [
  'scan-repo',
  'create-spec',
  'run-workflow',
  'security-review',
  'view-specs',
];

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

interface ActionCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  buttonLabel: string;
  onClick: () => void;
  disabled?: boolean;
  disabledMessage?: string;
  badge?: string;
  color?: 'blue' | 'purple' | 'green' | 'gray' | 'indigo' | 'amber' | 'red';
}

const ActionCard: React.FC<ActionCardProps> = ({
  icon,
  title,
  description,
  buttonLabel,
  onClick,
  disabled = false,
  disabledMessage = 'Upgrade your plan to access this feature',
  badge,
  color = 'blue',
}) => {
  const colorClasses: Record<string, {
    bg: string;
    border: string;
    iconBg: string;
    iconText: string;
    hover: string;
  }> = {
    blue: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      iconBg: 'bg-blue-100',
      iconText: 'text-blue-600',
      hover: 'hover:border-blue-300 hover:shadow-md',
    },
    purple: {
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      iconBg: 'bg-purple-100',
      iconText: 'text-purple-600',
      hover: 'hover:border-purple-300 hover:shadow-md',
    },
    green: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      iconBg: 'bg-green-100',
      iconText: 'text-green-600',
      hover: 'hover:border-green-300 hover:shadow-md',
    },
    indigo: {
      bg: 'bg-indigo-50',
      border: 'border-indigo-200',
      iconBg: 'bg-indigo-100',
      iconText: 'text-indigo-600',
      hover: 'hover:border-indigo-300 hover:shadow-md',
    },
    amber: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      iconBg: 'bg-amber-100',
      iconText: 'text-amber-600',
      hover: 'hover:border-amber-300 hover:shadow-md',
    },
    red: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      iconBg: 'bg-red-100',
      iconText: 'text-red-600',
      hover: 'hover:border-red-300 hover:shadow-md',
    },
    gray: {
      bg: 'bg-gray-50',
      border: 'border-gray-200',
      iconBg: 'bg-gray-100',
      iconText: 'text-gray-500',
      hover: '',
    },
  };

  const colors = disabled ? colorClasses.gray : colorClasses[color] || colorClasses.blue;

  const cardContent = (
    <div
      className={`
        relative p-5 rounded-lg border transition-all duration-200
        ${colors.bg} ${colors.border}
        ${disabled ? 'opacity-70 cursor-not-allowed' : colors.hover + ' cursor-pointer'}
      `}
      onClick={disabled ? undefined : onClick}
    >
      {/* Badge */}
      {badge && (
        <span className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
          {badge}
        </span>
      )}

      {/* Icon */}
      <div className={`w-10 h-10 rounded-lg ${colors.iconBg} flex items-center justify-center mb-3`}>
        <div className={colors.iconText}>
          {disabled ? <HiLockClosed className="w-5 h-5" /> : icon}
        </div>
      </div>

      {/* Title */}
      <h3 className={`font-semibold mb-1 ${disabled ? 'text-gray-500' : 'text-gray-900'}`}>
        {title}
      </h3>

      {/* Description */}
      <p className={`text-sm mb-3 ${disabled ? 'text-gray-400' : 'text-gray-600'}`}>
        {description}
      </p>

      {/* Action Button */}
      {disabled ? (
        <Button size="xs" color="gray" disabled className="cursor-not-allowed">
          <HiSparkles className="w-3 h-3 mr-1" />
          Upgrade
        </Button>
      ) : (
        <Button size="xs" color={color === 'gray' ? 'gray' : 'light'}>
          {buttonLabel}
          <HiArrowRight className="w-3 h-3 ml-1" />
        </Button>
      )}
    </div>
  );

  if (disabled) {
    return (
      <Tooltip content={disabledMessage} placement="bottom">
        {cardContent}
      </Tooltip>
    );
  }

  return cardContent;
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const QuickActionsBar: React.FC<QuickActionsBarProps> = (props) => {
  // Handle legacy props
  if (isLegacyProps(props)) {
    const {
      onScanRepo,
      onUploadDocs,
      onViewSpecs,
      canUpload,
      canGenerateSpecs,
      isLoading = false,
      className = '',
    } = props;

    if (isLoading) {
      return (
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${className}`}>
          {[1, 2, 3].map((i) => (
            <div key={i} className="p-5 rounded-lg border border-gray-200 bg-gray-50 animate-pulse">
              <div className="w-10 h-10 rounded-lg bg-gray-200 mb-3" />
              <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
              <div className="h-4 bg-gray-200 rounded w-full mb-3" />
              <div className="h-8 bg-gray-200 rounded w-24" />
            </div>
          ))}
        </div>
      );
    }

    return (
      <Card className={className}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Quick Actions</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Scan Repository */}
          <ActionCard
            icon={<FaGithub className="w-5 h-5" />}
            title="Scan Repository"
            description="Connect a GitHub repository for automated code analysis and health scoring."
            buttonLabel="Scan Now"
            onClick={onScanRepo}
            color="blue"
          />

          {/* Upload Documents */}
          <ActionCard
            icon={<HiDocumentAdd className="w-5 h-5" />}
            title="Upload Documents"
            description="Import existing docs, PRDs, meeting notes, and architecture documents."
            buttonLabel="Upload"
            onClick={onUploadDocs}
            disabled={!canUpload}
            disabledMessage="Document upload is available on Starter plan and above"
            color="purple"
          />

          {/* View Specifications */}
          <ActionCard
            icon={<HiDocumentText className="w-5 h-5" />}
            title="View Specifications"
            description="Browse and manage your generated specifications and documentation."
            buttonLabel="View Specs"
            onClick={onViewSpecs}
            disabled={!canGenerateSpecs}
            disabledMessage="Specification generation is available on Starter plan and above"
            color="green"
          />
        </div>
      </Card>
    );
  }

  // Handle extended props
  const {
    onActionClick,
    visibleActions = onboardingActions,
    disabledActions = [],
    disabledMessages = {},
    badges = {},
    compact = false,
    isLoading = false,
    className = '',
  } = props;

  // Loading state
  if (isLoading) {
    const gridCols = compact ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-1 md:grid-cols-3';
    return (
      <div className={`grid ${gridCols} gap-4 ${className}`}>
        {visibleActions.map((_, i) => (
          <div key={i} className="p-5 rounded-lg border border-gray-200 bg-gray-50 animate-pulse">
            <div className="w-10 h-10 rounded-lg bg-gray-200 mb-3" />
            <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
            <div className="h-4 bg-gray-200 rounded w-full mb-3" />
            <div className="h-8 bg-gray-200 rounded w-24" />
          </div>
        ))}
      </div>
    );
  }

  // Build actions to display
  const actionsToShow: QuickAction[] = visibleActions
    .filter(id => defaultActionsConfig[id])
    .map(id => ({
      id,
      ...defaultActionsConfig[id],
      disabled: disabledActions.includes(id),
      disabledMessage: disabledMessages[id],
      badge: badges[id],
    }));

  // Compact mode - smaller buttons
  if (compact) {
    return (
      <div className={`flex flex-wrap gap-2 ${className}`}>
        {actionsToShow.map((action) => (
          <Tooltip key={action.id} content={action.description}>
            <Button
              size="sm"
              color={action.disabled ? 'gray' : (action.color === 'gray' ? 'gray' : 'light')}
              disabled={action.disabled}
              onClick={() => onActionClick(action.id)}
              className="relative"
            >
              {action.icon}
              <span className="ml-2">{action.label}</span>
              {action.badge && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {action.badge}
                </span>
              )}
            </Button>
          </Tooltip>
        ))}
      </div>
    );
  }

  // Full card view
  const gridCols = actionsToShow.length <= 3
    ? 'grid-cols-1 md:grid-cols-3'
    : actionsToShow.length <= 4
      ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
      : 'grid-cols-1 md:grid-cols-3 lg:grid-cols-5';

  return (
    <Card className={className}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Quick Actions</h2>
      </div>

      <div className={`grid ${gridCols} gap-4`}>
        {actionsToShow.map((action) => (
          <ActionCard
            key={action.id}
            icon={action.icon}
            title={action.label}
            description={action.description}
            buttonLabel={action.label.split(' ')[0]} // First word as button label
            onClick={() => onActionClick(action.id)}
            disabled={action.disabled}
            disabledMessage={action.disabledMessage}
            badge={action.badge}
            color={action.color}
          />
        ))}
      </div>
    </Card>
  );
};

export default QuickActionsBar;
